import { PageType, GetRouteAdTargeting } from 'common/routing/page-definition'
import { layout } from 'thewest/App.routing'
import { metrics } from 'component-library/__styling/settings'
import { ArticleV4DTO, PageMetaDTO } from 'publication-types'
import { BaseClientConfig } from 'common/client/BaseClientConfig'
import { regionalSiteConfig } from 'thewest/routes/regionals/regional-site-config'
import { ContentDataTypes } from 'common/data/loader-definitions'
import { RenderTarget } from 'common/context-providers/render-target-context'
import { getAmpArticleComposition } from './article.amp'
import { AdTargeting } from 'common/advertising/add-ads'
import { TheWestSection } from 'component-library/__styling/settings/sections/thewest'
import { renderForPrimaryTopic } from 'common/routing/helpers'

// Used for Quicklinks
export const publicationPageType = 'publication'

export const createArticleRoute = ({
    article,
    config,
    section,
    getAdTargeting,
    pageMeta,
    renderTarget,
}: {
    article: ArticleV4DTO
    config: BaseClientConfig
    section: TheWestSection
    getAdTargeting: GetRouteAdTargeting
    renderTarget: RenderTarget
    pageMeta?: PageMetaDTO
}): PageType<typeof layout, TheWestSection> => {
    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        'article',
        section,
        article.topics.primary,
        ...article.topics.secondary
    )

    const adTargeting: AdTargeting = {
        pageId: article.slug,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    return {
        kind: 'page',
        heading: article.heading,
        pageMeta: {
            ...pageMeta,
            description: article.homepageTeaser,
            title: article.seoHead || undefined,
        },
        socialMeta: {
            title: article.socialHead || article.homepageHead,
            description: article.socialTeaser || article.homepageTeaser,
        },
        hideHeading: true,
        noMetaTitleTemplate: false,
        pageType: publicationPageType,
        adTargeting,
        additionalPageProperties: {
            primaryTopic: article.topics.primary,
            secondaryTopics: article.topics.secondary,
            publicationKind: article.kind,
            // DPT-1375 extra Metadata for variant testing. Remove when finished
            isPremium: article.isPremium,
            layout: article.layoutView,
        },
        section,
        compositions:
            renderTarget === 'amp'
                ? [getAmpArticleComposition(article, section)]
                : [
                      layout.composition({
                          type: 'box',
                          props: {
                              containerWidth: metrics.thewest.mainContentWidth,
                              horizontalGutters: 'outerMargin',
                              verticalGutters: ['outerMargin', 'unset'],
                          },
                          contentAreas: {
                              main: [
                                  layout.component({
                                      type: 'ad-unit',
                                      feature: 'outstream',
                                      props: {
                                          noticePosition: 'none',
                                          hiddenUntilLoaded: false,
                                          padding: [0],
                                          slot: {
                                              id: 'outstream2x2',
                                              size: 'outstream',
                                          },
                                      },
                                  }),
                                  layout.component({
                                      type: 'ad-unit',
                                      feature: 'ad-tech-dfp-testing',
                                      props: {
                                          noticePosition: 'none',
                                          hiddenUntilLoaded: false,
                                          padding: [0],
                                          slot: {
                                              id: '2x1_test_pixel',
                                              size: '2x1_test_pixel',
                                          },
                                      },
                                  }),
                                  ...getBreakingNewsStrap(section),
                              ],
                          },
                      }),
                      // Only show cricket widget on non-regional pages
                      ...(section === 'default'
                          ? [
                                layout.composition({
                                    type: 'box',
                                    feature: 'cricket-widgets',
                                    props: {
                                        containerWidth: metrics.thewest.mainContentWidth,
                                        horizontalGutters: 'outerMargin',
                                        verticalGutters: ['outerMargin', 'unset'],
                                    },
                                    contentAreas: {
                                        main: [
                                            ...renderForPrimaryTopic(
                                                article.topics.primary,
                                                {
                                                    allowedTopics: [
                                                        'sport',
                                                        'sport/cricket',
                                                        'sport/australian-cricket-team',
                                                        'sport/big-bash-league',
                                                        'sport/womens-cricket',
                                                        'sport/perth-scorchers',
                                                    ],
                                                },
                                                [
                                                    layout.component({
                                                        type: 'HorizontalCricketWidget',
                                                        props: {},
                                                    }),
                                                ]
                                            ),
                                        ],
                                    },
                                }),
                            ]
                          : []),
                      layout.composition({
                          type: 'zeus',
                          props: {
                              containerWidth: metrics.thewest.mainContentWidth,
                              horizontalGutters: false,
                              hasBackgroundFill: true,
                              stretchSelf: true,
                          },
                          contentAreas: {
                              main: [
                                  layout.nestedComposition({
                                      type: 'box',
                                      props: {
                                          horizontalGutters: 'outerMargin',
                                          verticalGutters: ['unset', 'md'],
                                          breakpoints: {
                                              sm: {
                                                  horizontalGutters: 'lg',
                                              },
                                          },
                                      },
                                      contentAreas: {
                                          main: [
                                              layout.component({
                                                  type: 'article',
                                                  props: {
                                                      meta: {
                                                          slug: article.slug,
                                                          kind: 'article',
                                                          inlinePublicationContent: [
                                                              {
                                                                  kind: 'ad',
                                                                  insertAfter: 3,
                                                                  content: [
                                                                      {
                                                                          noticePosition: 'none',
                                                                          hiddenUntilLoaded: true,
                                                                          padding: [
                                                                              0,
                                                                              0,
                                                                              metrics.thewest
                                                                                  .margins.md,
                                                                              0,
                                                                          ],
                                                                          slot: {
                                                                              id:
                                                                                  'article-mrec-one',
                                                                              size: 'below768Mrec',
                                                                              pageType: 'article',
                                                                          },
                                                                      },
                                                                      {
                                                                          noticePosition: 'none',
                                                                          hiddenUntilLoaded: true,
                                                                          padding: [
                                                                              0,
                                                                              0,
                                                                              metrics.thewest
                                                                                  .margins.md,
                                                                              0,
                                                                          ],
                                                                          slot: {
                                                                              id: 'article-banner',
                                                                              size: 'articleBanner',
                                                                              pageType: 'article',
                                                                          },
                                                                      },
                                                                  ],
                                                              },
                                                              {
                                                                  kind: 'oovvuu-featured-card',
                                                                  insertAfter: 7,
                                                                  props: {
                                                                      oovvuuUrl: config.oovvuuUrl,
                                                                      oovvuuPath: config.oovvuuPath,
                                                                  },
                                                              },
                                                          ],
                                                      },
                                                      dataDefinitionArgs: {
                                                          publicationId: article.id,
                                                      },
                                                      section,
                                                      isVideoStickyEnabled: true,
                                                  },
                                              }),
                                              layout.component({
                                                  type: 'promotional-card',
                                                  props: {
                                                      fontScale: 1,
                                                      verticalSpacing: 'lg',
                                                      fixedRatio: '4:3',
                                                      hasBackground: true,
                                                      hasPadding: true,
                                                      mediaMode: 'edgeToEdge',
                                                      showCTA: true,
                                                      dataDefinitionArgs: {
                                                          type: 'promotional',
                                                          metadataKey: `${
                                                              article.topics.primary.parent
                                                                  ? article.topics.primary.parent.id
                                                                  : article.topics.primary.id
                                                          }-promotional-card`,
                                                          pageSize: 1,
                                                      },
                                                      cardOrientation: {
                                                          defaultLayout: 'landscape',
                                                      },
                                                  },
                                              }),
                                              layout.component({
                                                  type: 'topic-list',
                                                  props: {
                                                      topics: article.topics,
                                                      verticalSpacing: 'md',
                                                  },
                                              }),
                                              ...articleFollowers(section),
                                              layout.component({
                                                  type: 'ad-unit',
                                                  props: {
                                                      noticePosition: 'none',
                                                      padding: [metrics.thewest.margins.md, 0],
                                                      slot: {
                                                          id: 'article-mrec-two',
                                                          size: 'mobileMrec',
                                                          pageType: 'article',
                                                      },
                                                  },
                                              }),
                                          ],
                                      },
                                  }),
                              ],
                              sidebar: [
                                  layout.component({
                                      type: 'ad-unit',
                                      props: {
                                          noticePosition: 'none',
                                          padding: [0, 0, metrics.thewest.margins.md, 0],
                                          slot: {
                                              id: 'sidebar-top',
                                              size: 'desktopMrecHalfPage',
                                              pageType: 'article',
                                          },
                                      },
                                  }),
                                  layout.nestedComposition({
                                      type: 'box',
                                      props: {
                                          stickyOffset: 350,
                                          fillContainer: true,
                                      },
                                      contentAreas: {
                                          main: [
                                              layout.component({
                                                  type: 'ad-unit',
                                                  props: {
                                                      noticePosition: 'none',
                                                      padding: [
                                                          0,
                                                          0,
                                                          metrics.thewest.margins.md,
                                                          0,
                                                      ],
                                                      slot: {
                                                          id: 'sidebar-middle',
                                                          size: 'desktopMrec',
                                                          pageType: 'article',
                                                      },
                                                  },
                                              }),

                                              layout.component({
                                                  type: 'juliet',
                                                  props: {
                                                      cardType: {
                                                          type: 'landscape',
                                                          format: 'landscape-smallImage',
                                                      },
                                                      removeHorizontalGutters: true,
                                                      hideKicker: true,
                                                      contentIsTangential: true,
                                                      sectionHeader: {
                                                          heading: getSidebarHeading(section),
                                                          section: 'generic',
                                                      },
                                                      dataDefinitionArgs: getSidebarDataDefinition(
                                                          section
                                                      ),
                                                  },
                                              }),
                                          ],
                                      },
                                  }),
                              ],
                          },
                      }),
                      layout.composition({
                          type: 'thor',
                          feature: ['new-outbrain-position', true],
                          props: {
                              containerWidth: 1250,
                          },
                          contentAreas: {
                              main: [
                                  layout.component({
                                      type: 'outbrain-ad',
                                      props: {
                                          link: config.outbrainUrl,
                                          widgetId: 'AR_1',
                                          template: 'TheWestAustralian',
                                      },
                                  }),
                              ],
                          },
                      }),
                  ],
    }
}

const getSidebarHeading = (section: TheWestSection) => {
    if (section === 'default') {
        // the west
        return 'Top Stories'
    }

    // A regional masthead
    return 'Local News'
}

const articleFollowers = (section: TheWestSection) => {
    // Is this the west of a regional masthead?
    const isTheWest = section === 'default'

    const components = []

    if (isTheWest) {
        components.push(
            layout.component({
                feature: 'newsletter-subscription-banner',
                type: 'newsletter-signup',
                props: {
                    text: 'The latest from The West delivered to your inbox daily.',
                    buttonLabel: 'Sign Up',
                },
            })
        )
    }

    return components
}

const getSidebarDataDefinition = (section: TheWestSection): ContentDataTypes => {
    if (section !== 'default') {
        const regionalConfig = regionalSiteConfig[section]
        if (regionalConfig) {
            return {
                type: 'listing',
                topics: [regionalConfig.mastheadTopic],
                paging: {
                    page: 1,
                    pageSize: 3,
                },
            }
        }
    }

    return {
        type: 'curation',
        name: 'home',
        offset: 0,
        pageSize: 3,
    }
}

const getBreakingNewsStrap = (section: TheWestSection) => {
    if (section !== 'default') {
        // For now at least we dont want the breaking news strap on any regional masthead
        return []
    }

    return [
        layout.component({
            type: 'breaking-news',
            props: {
                dataDefinitionArgs: {
                    type: 'curation',
                    name: 'breaking-news',
                    offset: 0,
                    pageSize: 1,
                },
            },
        }),
    ]
}
